import gql from "graphql-tag";
import { FloatSpotlightFragment } from "@graphqlTypes";
import { useRef, useEffect } from "react";
import { Button, Container, Image } from "@components";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import useParallax from "../Parallax/useParallax";
import { useInView } from "motion/react";
import { useViewSize } from "@/providers/ViewSizeContext";
import { AnimVals } from "@/utils/animUtils";

import styles from "./FloatSpotlight.module.scss";
import className from "classnames/bind";
const cx = className.bind(styles);

type FloatSpotlightProps = Omit<FloatSpotlightFragment, '__typename'>;

export default function FloatSpotlight(props: FloatSpotlightProps) {
  const { eyebrow, headline, cta, images, smallText, secondaryCta } = props;
  const hasCTA = cta && cta.label && (cta.page?.node || cta.externalLink);
  const hasSecondaryCTA = secondaryCta && secondaryCta?.label && (secondaryCta?.page?.node || secondaryCta?.externalLink);
  const [ctaType] = cta?.linkType || [];
  const [secondaryCtaType] = secondaryCta?.linkType || [];
  const showButtons = hasCTA || hasSecondaryCTA;
  const headlineSize = smallText ? "title-lc-m" : "title-lc-l";

  // -------------------- Refs & State

  const rootEl = useRef<HTMLElement | null>(null);
  const eyebrowEl = useRef<HTMLParagraphElement | null>(null);
  const headlineEl = useRef<HTMLParagraphElement | null>(null);
  const buttonWrapperEl = useRef<HTMLDivElement | null>(null);
  const timelineAnimIn = useRef<gsap.core.Timeline | null>(null);

  // Images: Store wrapper refs by key
  const imageRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Images: Speed mapping (used by data-speed and influences stagger/duration)
  const imageSpeeds: Record<string, number> = {
    topLeft: 2.5,
    top: 2,
    topRight: 3,
    bottomLeft: 3,
    bottomRight: 1.5,
  };

  // -------------------- Parallax

  useParallax({ containerRef: rootEl, distancePx: 9999/*overwritten*/, isEnabled: true, start: 'top bottom', end: 'bottom top' });

  const baseDistancePx = 72;

  // Note: The containerRef is searched for `js-parallax` elements, which each
  // get their own distance based on data attributes.
  // The distance is computed as: baseDistancePx * data-speed.
  // The `distancePx` value above is not used. -Gabe

  // -------------------- Animation

  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = 0.35; // Note: custom value for this component. -Gabe
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  // Timeline initialization
  useGSAP(() => {
    if (timelineAnimIn.current) timelineAnimIn.current.kill();

    timelineAnimIn.current = gsap.timeline({ paused: true }).add("start");

    const tl = timelineAnimIn.current;
    const gDur = 0.7;

    // Images: staggered reveal - compute array of refs
    const imageEntries = images
      ? Object.entries(images).filter(([_, img]) => typeof img === "object")
      : [];

    if (imageEntries.length > 0) {
      const refs = imageEntries
        .map(([key]) => imageRefs.current[key])
        .filter(Boolean) as HTMLDivElement[];

      // Stagger speeds by reading imageSpeeds: generate an order & durations
      tl.add("images-in");
      tl.fromTo(
        refs,
        {
          opacity: 0,
          y: "2rem",
          scale: 0.98
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: gDur,
          ease: "power3.out",
          stagger: {
            each: 0.08,
            from: "start"
          }
        },
        "images-in"
      );
    }

    // ---------- Eyebrow
    if (eyebrowEl.current) {
      tl.fromTo(
        eyebrowEl.current,
        { opacity: 0, y: "0.6rem" },
        { opacity: 1, y: 0, duration: gDur * 0.6, ease: "power3.out" },
        "start+=0.1"
      );
    }

    // ---------- Headline
    if (headlineEl.current) {
      tl.fromTo(
        headlineEl.current,
        { opacity: 0, y: "1.2rem" },
        { opacity: 1, y: 0, duration: gDur, ease: "power3.out" },
        "start+=0.3"
      );
    }

    // ---------- Buttons
    if (buttonWrapperEl.current) {
      tl.fromTo(
        buttonWrapperEl.current,
        { opacity: 0, y: "1rem" },
        { opacity: 1, y: 0, duration: gDur * 0.8, ease: "power3.out" },
        "start+=0.6"
      );
    }

  }, [images, eyebrow, headline, cta, secondaryCta]);

  // Timeline control based on isInView
  useEffect(() => {
    const tl = timelineAnimIn.current;
    if (!tl) return;
    if (isInView) {
      tl.restart(); // Ensure we start from the beginning
      tl.play();
    }
  }, [isInView]);

  // -------------------- Render

  return (
    <section className={cx(["float-spotlight"])} ref={rootEl}>

      <Container className={cx(["container"])}>
        <div className={cx(["content"])}>
          <p className={cx(["eyebrow-l", "eyebrow"])} ref={eyebrowEl}>{eyebrow}</p>
          <p className={cx([headlineSize, "headline"])} ref={headlineEl}>{headline}</p>
          { showButtons && (
            <div className={cx(["button-wrapper"])} ref={buttonWrapperEl}>
              {hasCTA && (
                <Button
                  className={cx(["cta"])}
                  url={ctaType === "external" ? cta.externalLink : cta.page?.node?.uri}
                  label={cta.label}
                />
              )}
              {hasSecondaryCTA && (
                <Button
                  className={cx(["secondary-cta"])}
                  url={secondaryCtaType === "external" ? secondaryCta.externalLink : secondaryCta.page?.node?.uri}
                  label={secondaryCta.label}
                />
              )}
            </div>
          )}
        </div>
      </Container>

      <div className={cx(["image-container"])}>
        {images &&
          Object.keys(images).length > 0 &&
            Object.entries(images).map(([key, image]) => {
              if (typeof image !== "object") return null;

              // Compute distance from image speed
              // Higher speed = more movement. -Gabe
              const speed = imageSpeeds[key] ?? 1.5;
              const distancePx = Math.max(2, (baseDistancePx * speed));

              return (
                <div
                  className={cx(["image", `image--${key}`])}
                  key={key}
                  ref={(el) => { imageRefs.current[key] = el; }}
                  data-speed={imageSpeeds[key] ?? 1.5}
                >
                  <Image
                    image={image}
                    className="js-parallax"
                    data-end-px={-distancePx}
                    data-start-px={distancePx}
                  />
                </div>
              );
            }
          )
        }
        <div className={cx(["image-wrapper-overlay"])}/>
      </div>

    </section>
  );

}

FloatSpotlight.fragments = {
  entry: gql`
    fragment FloatSpotlight on FlexiblePageContentContentFloatSpotlightLayout {
      eyebrow
      headline
      smallText
      cta {
        linkType
        externalLink
        label
        page {
          node {
            uri
          }
        }
      }
      secondaryCta {
        linkType
        externalLink
        label
        page {
          node {
            uri
          }
        }
      }
      images {
        topLeft {
          ...ACFMedia
        }
        top {
          ...ACFMedia
        }
        topRight {
          ...ACFMedia
        }
        bottomLeft {
          ...ACFMedia
        }
        bottomRight {
          ...ACFMedia
        }
      }
    }
  `,
};
